/**
 * «No aplica» por FILA en la tabla del Explorador / Informes (tanda «Feedback de Hesta», contrato §2).
 *
 * Puro, sin React: lo usan la tabla (`DataExplorerTable`) y el PDF (`utils/pdfExport.js`), para que
 * la pantalla y el archivo digan lo mismo.
 *
 * El motor manda 0 en las métricas que no corresponden a la plataforma de la fila (GA4 en una fila de
 * Google Ads, el costo en la fila de GA4): es a propósito, para que las sumas no cuenten dos veces.
 * La tabla lo traduce al mostrar: «—» en esa celda, sin badge de variación, ordenada al final y fuera
 * del mapa de calor. El motor, el Total, el Tablero, el MCP y Looker no cambian.
 *
 * - **Sólo actúa con la dimensión Plataforma (`Canal`) en la tabla.** Sin ella una fila puede mezclar
 *   fuentes y no hay forma de saber qué corresponde: la tabla queda como siempre.
 * - **A qué canales aplica cada métrica** lo dice el catálogo (`FIELD_APPLIES_TO_CHANNELS`, contrato
 *   §1). Una métrica que no está en el mapa aplica a TODAS (fórmulas, personalizadas, campos de la
 *   línea): el modo de fallo es no pintar «—», nunca pintarlo de más. Sin la clave (un backend que
 *   todavía no la manda) se deduce del grupo del campo con `fieldKind`: GA4 → sólo `ANALYTICS`,
 *   tienda → los canales de tienda, pauta → el resto, lo demás → todas.
 * - **Un número distinto de 0 nunca se pinta «—»** (ni se oculta con «Ocultar filas sin datos»),
 *   diga lo que diga el mapa: el «—» es para el 0 de relleno del motor. Si el mapa se equivoca, la
 *   celda muestra el dato en vez de esconderlo (review C-tabla-01).
 */

import {KIND, fieldKind} from '../../utils/filterNotes';
import {isAdditiveMetric} from './undisaggregatedResiduals';

export const CHANNEL_KEY = 'dimension_Canal';
export const CAMPAIGN_KEY = 'dimension_Campana';
export const ADGROUP_KEY = 'dimension_Grupo_de_Anuncios';
export const AD_KEY = 'dimension_Anuncio';
export const GA4_CHANNEL = 'ANALYTICS';

// Las columnas de la pauta que dicen de dónde vino una fila: la campaña y, desde el cruce de GA4 por
// grupo y anuncio (tanda «Cruce paid media ↔ GA4»), el grupo y el anuncio. Con el cruce, el GA4 que
// coincide sale en su grupo o anuncio, y el de la campaña que no coincide, en «(Sin identificar)»: lo
// que queda con todas estas columnas vacías es tráfico que no vino de la pauta. Los ID cuentan igual
// que los nombres: una fila con el ID de su campaña y el grupo vacío ES de esa campaña.
const AD_ENTITY_KEYS = Object.freeze([
    CAMPAIGN_KEY, 'dimension_Campana_ID', ADGROUP_KEY, 'dimension_Grupo_de_Anuncios_ID', AD_KEY, 'dimension_Anuncio_ID',
]);
// Dónde va el rótulo cuando la tabla no tiene Plataforma, en este orden: una columna con nombre.
const AD_LABEL_KEYS = Object.freeze([CAMPAIGN_KEY, ADGROUP_KEY, AD_KEY]);
const STORE_CHANNELS = ['SHOPIFY', 'TIENDA_NUBE', 'WOOCOMMERCE', 'VTEX'];

// Espejo A MANO de `_EXTRA_CHANNELS_BY_FIELD` del backend (`looker_cc/field_channels.py`): canales
// donde la métrica trae un valor real aunque su grupo diga otra cosa. Sólo lo usa el fallback (sin
// `FIELD_APPLIES_TO_CHANNELS`); con el mapa, manda el backend. `Eventos_Seleccionados` es del grupo
// Performance, pero el nodo pone las conversiones de las líneas gobernadas por GA4 en filas
// `ANALYTICS` (`getGa4GovernedEventsRows`, card AfhSbirf).
const EXTRA_CHANNELS_BY_FIELD = Object.freeze({
    Eventos_Seleccionados: [GA4_CHANNEL],
});

// Las métricas GA4 que el nodo sirve desde BigQuery (`GA4_FIELDS` de `sqls.js`): las únicas que se
// reparten por campaña con el vínculo GA4↔campaña, y las cinco son sumas. El «% asignado a campañas»
// sólo tiene sentido para ellas: en una tasa o un promedio por sesión, sumar filas no da una parte
// del total (review C-tabla-03). Si el nodo suma una métrica a esa lista, va también acá.
const GA4_SPLIT_BY_CAMPAIGN_METRICS = Object.freeze([
    'GA4__Conversiones',
    'GA4__Transacciones_de_comercio_electrónico',
    'GA4__Ingresos_por_compras',
    'GA4__Sesiones',
    'GA4__Usuario_activos',
]);

/** Cómo se rotula una fila de GA4: `UNATTRIBUTED` = «GA4 · sin campaña de pauta», `PLAIN` = «GA4». */
export const GA4_ROW = Object.freeze({UNATTRIBUTED: 'UNATTRIBUTED', PLAIN: 'PLAIN'});

function isMetricKey(headerKey) {
    return !!headerKey && headerKey.startsWith('metric_');
}

function metricIdOf(headerKey) {
    return headerKey.replace(/^metric_/, '');
}

function hasColumn(headers, key) {
    return (headers || []).some((h) => h && h.key === key);
}

/** El valor crudo de una dimensión: `{value, formattedValue}` o el valor pelado. */
function dimensionValue(cell) {
    if (cell && typeof cell === 'object') {
        if (cell.value !== undefined && cell.value !== null) return cell.value;
        return cell.formattedValue !== undefined ? cell.formattedValue : null;
    }
    return cell;
}

function isBlank(value) {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/** El valor del período actual de una celda de métrica (los dos shapes del backend o el número pelado). */
export function currentValueOf(cell) {
    if (cell && typeof cell === 'object') {
        if (cell.currentValue !== undefined) return cell.currentValue;
        if (cell.value !== undefined) return cell.value;
        return null;
    }
    return cell;
}

/** El de la comparación, o `null` si la celda no la trae. */
function comparisonValueOf(cell) {
    return cell && typeof cell === 'object' && cell.comparisonValue !== undefined ? cell.comparisonValue : null;
}

/** ¿Sin dato? `null`, vacío o 0. Un texto que no es número cuenta como dato. */
function isEmptyValue(value) {
    if (isBlank(value)) return true;
    const number = Number(value);
    return !Number.isNaN(number) && number === 0;
}

/** La plataforma de la fila (`GOOGLE`, `FACEBOOK`, `ANALYTICS`…) o `null` si la tabla no la trae. */
export function rowChannel(row) {
    const value = dimensionValue(row && row[CHANNEL_KEY]);
    return typeof value === 'string' && value.trim() ? value.trim().toUpperCase() : null;
}

function appliesByCatalog(metricId, channel, fieldsData) {
    const map = fieldsData && fieldsData.FIELD_APPLIES_TO_CHANNELS;
    if (map && typeof map === 'object') {
        const channels = map[metricId];
        if (!Array.isArray(channels)) return true;
        return channels.some((c) => String(c).toUpperCase() === channel);
    }
    if ((EXTRA_CHANNELS_BY_FIELD[metricId] || []).includes(channel)) return true;
    switch (fieldKind(metricId, fieldsData)) {
        case KIND.GA4:
            return channel === GA4_CHANNEL;
        case KIND.STORE:
            return STORE_CHANNELS.includes(channel);
        case KIND.PAID:
            return channel !== GA4_CHANNEL && !STORE_CHANNELS.includes(channel);
        default:
            return true;
    }
}

// La respuesta por (métrica, canal) no cambia mientras no cambie el catálogo: se calcula una vez por
// catálogo. La tabla la pregunta por celda, en el orden y en el mapa de calor (review C-tabla-05).
const appliesCache = new WeakMap();

/** ¿La métrica corresponde a ese canal? Ante la duda, sí. */
export function metricAppliesToChannel(metricId, channel, fieldsData) {
    if (!metricId || !channel) return true;
    if (!fieldsData || typeof fieldsData !== 'object') return appliesByCatalog(metricId, channel, fieldsData);
    let cache = appliesCache.get(fieldsData);
    if (!cache) {
        cache = new Map();
        appliesCache.set(fieldsData, cache);
    }
    const key = `${metricId}\u0000${channel}`;
    if (!cache.has(key)) cache.set(key, appliesByCatalog(metricId, channel, fieldsData));
    return cache.get(key);
}

/** ¿La celda trae un número distinto de 0 en el período o en la comparación? */
function hasRealValue(cell) {
    return !isEmptyValue(currentValueOf(cell)) || !isEmptyValue(comparisonValueOf(cell));
}

/**
 * ¿Esta celda dice «—»? Sólo para métricas, sólo si la fila trae Plataforma, y nunca sobre un número
 * real: lo que se pinta «—» es el 0 de relleno del motor.
 */
export function isNotApplicableCell(row, headerKey, fieldsData) {
    if (!isMetricKey(headerKey)) return false;
    const channel = rowChannel(row);
    if (!channel) return false;
    if (metricAppliesToChannel(metricIdOf(headerKey), channel, fieldsData)) return false;
    return !hasRealValue(row[headerKey]);
}

/** Las plataformas de las filas donde la métrica no aplica, sin repetir y en el orden en que aparecen. */
export function notApplicableChannels(rows, headerKey, fieldsData) {
    const channels = [];
    (rows || []).forEach((row) => {
        if (!isNotApplicableCell(row, headerKey, fieldsData)) return;
        const channel = rowChannel(row);
        if (!channels.includes(channel)) channels.push(channel);
    });
    return channels;
}

/**
 * ¿La fila no tiene datos? Todas sus métricas en 0, `null` o «—», en el período Y en la comparación:
 * una caída a 0 (0 contra 500) no se oculta. Sin métricas, nunca.
 */
export function isEmptyRow(row, headers, fieldsData) {
    const metricHeaders = (headers || []).filter((h) => h && isMetricKey(h.key));
    if (!metricHeaders.length) return false;
    return metricHeaders.every((h) => {
        if (isNotApplicableCell(row, h.key, fieldsData)) return true;
        const cell = row[h.key];
        return isEmptyValue(currentValueOf(cell)) && isEmptyValue(comparisonValueOf(cell));
    });
}

/** Las filas que se muestran: todas, o sin las vacías si el panel lo pide. */
export function rowsToShow(rows, headers, fieldsData, hideEmptyRows) {
    if (!hideEmptyRows) return rows || [];
    return (rows || []).filter((row) => !isEmptyRow(row, headers, fieldsData));
}

/** ¿La fila sólo tiene datos de métricas GA4? (y alguno distinto de cero) */
function hasOnlyGa4Data(row, headers, fieldsData) {
    let any = false;
    for (const h of headers || []) {
        if (!h || !isMetricKey(h.key)) continue;
        const cell = row[h.key];
        if (isEmptyValue(currentValueOf(cell)) && isEmptyValue(comparisonValueOf(cell))) continue;
        if (fieldKind(metricIdOf(h.key), fieldsData) !== KIND.GA4) return false;
        any = true;
    }
    return any;
}

/**
 * Cómo se rotula una fila de GA4, o `null` si no es una.
 *
 * «Sin pauta» = la tabla tiene alguna columna de la pauta (Campaña, Grupo de anuncios, Anuncio o sus
 * ID) y la fila las trae TODAS vacías.
 * - Con Plataforma: la fila `ANALYTICS` es «GA4 · sin campaña de pauta» si es «sin pauta», y «GA4» en
 *   cualquier otro caso (el código `ANALYTICS` no le dice nada a nadie).
 * - Sin Plataforma (el panel «Plataforma vs. GA4 por campaña», o grupo × costo × GA4): la fila «sin
 *   pauta» es la de GA4 sólo si TODO lo que tiene es de GA4; si suma ventas de la tienda u otra cosa,
 *   se deja como está. Y sólo si hay una columna con nombre donde rotularla: sin ella, ni la nota al pie
 *   del PDF.
 */
export function ga4RowKind(row, headers, fieldsData) {
    const entityKeys = AD_ENTITY_KEYS.filter((key) => hasColumn(headers, key));
    const withoutAdEntity = entityKeys.length > 0
        && entityKeys.every((key) => isBlank(dimensionValue(row && row[key])));
    if (hasColumn(headers, CHANNEL_KEY)) {
        if (rowChannel(row) !== GA4_CHANNEL) return null;
        return withoutAdEntity ? GA4_ROW.UNATTRIBUTED : GA4_ROW.PLAIN;
    }
    if (!ga4LabelColumn(headers)) return null;
    if (withoutAdEntity && hasOnlyGa4Data(row, headers, fieldsData)) return GA4_ROW.UNATTRIBUTED;
    return null;
}

/** En qué columna va el rótulo de la fila de GA4: Plataforma si está; si no, Campaña, Grupo o Anuncio. */
export function ga4LabelColumn(headers) {
    if (hasColumn(headers, CHANNEL_KEY)) return CHANNEL_KEY;
    return AD_LABEL_KEYS.find((key) => hasColumn(headers, key)) || null;
}

/**
 * Qué parte de una métrica GA4 quedó asignada a campañas, de 0 a 1, o `null` si no corresponde: la
 * tabla no tiene Campaña, la métrica no es una de las que se reparten por campaña
 * (`GA4_SPLIT_BY_CAMPAIGN_METRICS`: nunca una tasa ni un promedio), o no hay nada que repartir.
 * Se calcula sobre las filas del período actual: lo de las filas con campaña sobre el total de las filas.
 */
export function ga4AssignedShare(rows, headers, headerKey, fieldsData) {
    if (!isMetricKey(headerKey) || !hasColumn(headers, CAMPAIGN_KEY)) return null;
    const metricId = metricIdOf(headerKey);
    if (!GA4_SPLIT_BY_CAMPAIGN_METRICS.includes(metricId) || !isAdditiveMetric(fieldsData, metricId)) return null;
    let total = 0;
    let assigned = 0;
    (rows || []).forEach((row) => {
        const value = Number(currentValueOf(row[headerKey]));
        if (!Number.isFinite(value) || value === 0) return;
        total += value;
        if (!isBlank(dimensionValue(row[CAMPAIGN_KEY]))) assigned += value;
    });
    if (!(total > 0)) return null;
    return Math.max(0, Math.min(1, assigned / total));
}

/**
 * El porcentaje como se lee: entero, y sin redondear a los extremos lo que no lo es (un 99,6 % no es
 * «100 %», ni un 0,3 % es «0 %»).
 */
export function formatShare(share, locale = 'es') {
    let percent = Math.round(share * 100);
    if (share < 1 && percent >= 100) percent = 99;
    if (share > 0 && percent <= 0) percent = 1;
    try {
        return new Intl.NumberFormat(locale, {style: 'percent', maximumFractionDigits: 0}).format(percent / 100);
    } catch (e) {
        return `${percent} %`;
    }
}
